import * as R from "ramda";
import React from "react";
import { FaDesktop, FaExpandArrowsAlt, FaMobileAlt, FaTabletAlt, FaTimes } from "react-icons/fa";
import { connect } from "react-redux";
import ToolTip from "react-tooltip";
import { Dispatch } from "redux";
import { Action } from "redux-actions";
import styled from "styled-components";
import * as actions from "../../actions/course-author/course-author";
import { positionQueryParameter } from "../../actions/course-author/helpers";
import { IState } from "../../App/reducer";
import { RequestState } from "../../App/types/common";
import ClickableElement from "../../components/utils/ClickableElement";
import CustomModal, {
  ModalBody as CustomModalBody,
  ModalCard,
  ModalHeader,
  ModalType,
} from "../../components/utils/CustomModal";
import { dependencies } from "../../dependencies";

const ModalBackground = styled.div`
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: grid;
  place-items: center;
  background-color: #0000009e;
  position: fixed;
  z-index: 1000;
  cursor: pointer;
  overflow: auto;
`;

const ModalContent = styled.div<{ resolution: IResolution }>`
  background-color: #fff;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1), 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  position: relative;
  margin: 1rem;
  height: ${({ resolution }) => resolution.height};
  width: ${({ resolution }) => resolution.width};
`;

const CloseIcon = styled(ClickableElement)`
  padding: 1.5rem;
  border: 0.1rem solid ${({ theme }) => theme.colors.lightsilver};
  border-radius: 5rem;
  position: absolute;
  right: -1.5rem;
  top: -1.5rem;
  float: right;
  width: auto

  &: hover {
    color: ${({ theme }) => theme.colors.menuBlueLight};
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  z-index: 1001;
  cursor: default;
  height: calc(100% - 3rem);
`;

const CLOSE_TIME = 4000; // time in ms

const WarningModal = styled(CustomModal)<{ warningOpacity: number }>`
  background-color: transparent;
  top: unset;
  opacity: ${props => props.warningOpacity};
  transition: opacity ${CLOSE_TIME / 1000}s;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  height: unset;
  display: block;
  width: 70%;
  z-index: 1001;

  ${ModalHeader} {
    padding: 0;
  }

  ${ModalCard} {
    max-width: unset;
    max-height: unset;
  }

  ${CustomModalBody} {
    padding: 3rem 2rem;
  }

  @media (${({ theme }) => theme.breakpoints.min400}) {
    width: unset;
  }
`;

interface IResolution {
  height: string;
  width: string;
}

interface ICustomModalProps {
  showModal: boolean;
  closeModal: () => void;
  resolution: IResolution;
}

const PreviewModal: React.FC<ICustomModalProps> = props => {
  const { resolution } = props;
  const ref = React.useRef<HTMLDivElement>(null);
  const closeModalClickingBackground = (e: React.MouseEvent<HTMLDivElement>) =>
    e.target === ref.current && props.closeModal();
  return (
    <ModalBackground ref={ref} onClick={closeModalClickingBackground}>
      <ModalContent data-testid={`preview-container-${resolution.height}-${resolution.width}`} resolution={resolution}>
        <CloseIcon aria-label="close" onClick={props.closeModal}>
          <FaTimes />
        </CloseIcon>
        <ModalBody>{props.children}</ModalBody>
      </ModalContent>
    </ModalBackground>
  );
};

const ResolutionOptions = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  right: 3rem;
  top: 3rem;

  .hidden-options {
    display: none;
  }

  :hover {
    .hidden-options {
      display: flex;
    }
  }
`;

const HiddenOptions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background-color: ${({ theme }) => theme.colors.darkgrey1};
  border-radius: 1rem 0 0 1rem;
  padding: 0.7rem 1.2rem 0.7rem 0.7rem;
  z-index: 1000;
  position: relative;
  right: -1rem;

  svg {
    color: ${({ theme }) => theme.colors.darkgrey3};
    padding: 0 0.5rem;
  }
`;

const SelectedOption = styled.div`
  display: flex;
  padding: 1rem;
  border-radius: 1rem;
  background-color: ${({ theme }) => theme.colors.darkgrey2};
  z-index: 1001;

  svg {
    color: ${({ theme }) => theme.colors.white};
  }
`;

interface ICourseInstPreviewStateProps {
  courseInstId: string | null;
  displayPreviewCourseInst: boolean;
  displayPreviewCourseInstFailMessage: Nullable<string>;
  displayLPWarning: boolean;
  requestPreviewCourseState: RequestState;
}

interface ICourseInstPreviewDispatchProps {
  resetPreview: () => void;
  hideLPWarning: () => void;
}

interface ICourseInstPreviewOwnProps {
  search: string;
  isLinearProgressionEnabledInCourse: boolean;
}

type CourseInstPreviewProps = ICourseInstPreviewStateProps &
  ICourseInstPreviewDispatchProps &
  ICourseInstPreviewOwnProps;

const Resolution = {
  DESKTOP: { height: "1080px", width: "1920px" },
  FULL: { height: "90%", width: "90%" },
  MOBILE_LANDSCAPE: { height: "414px", width: "896px" },
  MOBILE_PORTRAIT: { height: "896px", width: "414px" },
  TABLET_LANDSCAPE: { height: "768px", width: "1024px" },
  TABLET_PORTRAIT: { height: "1024px", width: "768px" },
};

interface IResolutionOption {
  Icon: React.ElementType;
  resolution: IResolution;
}

interface IResolutionOptionProps {
  Icon: React.ElementType;
  resolution: IResolution;
  onSetResolution: (resolution: IResolution) => () => void;
}

const FaMobileLandscape = styled(FaMobileAlt)`
  transform: rotate(90deg);
`;

const FaTabletLandscape = styled(FaTabletAlt)`
  transform: rotate(90deg);
`;

const resolutionOptions: IResolutionOption[] = [
  { Icon: FaMobileLandscape, resolution: Resolution.MOBILE_LANDSCAPE },
  { Icon: FaMobileAlt, resolution: Resolution.MOBILE_PORTRAIT },
  { Icon: FaTabletLandscape, resolution: Resolution.TABLET_LANDSCAPE },
  { Icon: FaTabletAlt, resolution: Resolution.TABLET_PORTRAIT },
  { Icon: FaDesktop, resolution: Resolution.DESKTOP },
  { Icon: FaExpandArrowsAlt, resolution: Resolution.FULL },
];

const ResolutionOption: React.FC<IResolutionOptionProps> = ({ Icon, resolution, onSetResolution }) => {
  return (
    <>
      <Icon
        onClick={onSetResolution(resolution)}
        data-for={`resolution-${resolution.height}-${resolution.width}`}
        data-tip=""
        data-testid={`set-${resolution.height}-${resolution.width}`}
      />
      <ToolTip id={`resolution-${resolution.height}-${resolution.width}`} place="bottom">
        <p>{`H: ${resolution.height} W: ${resolution.width}`}</p>
      </ToolTip>
    </>
  );
};

const CourseInstPreview: React.FC<CourseInstPreviewProps> = props => {
  const [warningOpacity, setWarningOpacity] = React.useState(1);
  const [resolution, setResolution] = React.useState(Resolution.FULL);
  const closeModal = () => {
    setResolution(Resolution.FULL);
    props.resetPreview();
  };

  React.useEffect(() => {
    ToolTip.rebuild();
  }, [resolution]);

  React.useEffect(() => {
    // Waits before setting the opacity to 0, so that it gives time to the
    // node to start with opacity 1 and then transition to 0.
    const warningTimer = setTimeout(() => setWarningOpacity(0), 500);
    const timer = setTimeout(props.hideLPWarning, CLOSE_TIME);
    return () => {
      clearTimeout(timer);
      clearTimeout(warningTimer);
    };
  }, []);

  if (props.requestPreviewCourseState === RequestState.FAILED) {
    return (
      <CustomModal
        title="Course unavailable for preview"
        type={ModalType.Error}
        showModal={true}
        closeModal={closeModal}
        message={"Course can not be previewed. Please, contact support"}
      />
    );
  }

  if (!props.courseInstId) {
    return null;
  }

  const changeResolution = (newResolution: IResolution) => () => setResolution(newResolution);
  const isSelected = (resolutionOption: IResolutionOption) => resolution.height === resolutionOption.resolution.height;
  const hiddenOptions = R.reject(isSelected, resolutionOptions);
  const selectedOption = R.filter(isSelected, resolutionOptions);

  const url = new URL(`${dependencies.saltcomplianceApiRoot}/shc/learn.html`);
  url.searchParams.append("selectedCourseInstId", props.courseInstId);
  url.searchParams.append("embedded", "true");
  const currentPosition = positionQueryParameter(props.search);
  currentPosition && url.searchParams.append("startingPosition", currentPosition);
  const verifyBodyToClose = (e: React.SyntheticEvent<HTMLIFrameElement, Event>) => {
    // NOTE: The purpose of this method is to close the preview modal when the
    // user clicks on the exit options inside the iframe which converts the
    // innerHTML of the body tag into: '#/'.
    const iframeWindow = (e.target as HTMLIFrameElement).contentWindow;
    Array.from(iframeWindow?.document?.getElementsByTagName("body") ?? [])
      .filter(body => body.innerHTML === "#/")
      .forEach(closeModal);
  };

  return (
    <PreviewModal showModal={props.displayPreviewCourseInst} closeModal={closeModal} resolution={resolution}>
      <WarningModal
        type={ModalType.Warning}
        warningOpacity={warningOpacity}
        showModal={props.isLinearProgressionEnabledInCourse && props.displayLPWarning}
        closeModal={props.hideLPWarning}
        message={"Linear Progression has been disabled in the course preview."}
        title="Note"
      />
      <ResolutionOptions data-testid="options">
        <HiddenOptions className={"hidden-options"}>
          {hiddenOptions.map((option, idx) => (
            <ResolutionOption
              key={idx}
              Icon={option.Icon}
              resolution={option.resolution}
              onSetResolution={changeResolution}
            />
          ))}
        </HiddenOptions>
        <SelectedOption>
          {selectedOption.map((option, idx) => (
            <ResolutionOption
              key={idx}
              Icon={option.Icon}
              resolution={option.resolution}
              onSetResolution={changeResolution}
            />
          ))}
        </SelectedOption>
      </ResolutionOptions>
      <iframe data-testid="preview-course" src={url.href} height={"100%"} width={"100%"} onLoad={verifyBodyToClose} />
    </PreviewModal>
  );
};

const mapStateToProps = (state: IState): ICourseInstPreviewStateProps => ({
  courseInstId: state.App.CourseAuthor._.courseInstId,
  displayLPWarning: state.App.CourseAuthor._.displayLPWarning,
  displayPreviewCourseInst: state.App.CourseAuthor._.displayPreviewCourseInst,
  displayPreviewCourseInstFailMessage: state.App.CourseAuthor._.displayPreviewCourseInstFailMessage,
  requestPreviewCourseState: state.App.CourseAuthor._.requestPreviewCourseInstState,
});

const mapDispatchToProps = (dispatch: Dispatch<Action<any>>): ICourseInstPreviewDispatchProps => ({
  hideLPWarning: () => dispatch(actions.hideLPWarning()),
  resetPreview: () => dispatch(actions.resetPreviewCourseInst()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CourseInstPreview);
